import type { ReactNode } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type IssueItem = { id: string; title: string; icon: ReactNode; desc: string };

type Tone = "orange" | "emerald";

const tones: Record<Tone, { open: string; icon: string; btn: string }> = {
  orange: {
    open: "data-[state=open]:border-orange-500 data-[state=open]:ring-orange-500/20",
    icon: "bg-orange-50 text-orange-600 group-data-[state=open]:bg-orange-600 group-data-[state=open]:text-white",
    btn: "bg-orange-600 hover:bg-orange-700",
  },
  emerald: {
    open: "data-[state=open]:border-emerald-500 data-[state=open]:ring-emerald-500/20",
    icon: "bg-emerald-50 text-emerald-600 group-data-[state=open]:bg-emerald-600 group-data-[state=open]:text-white",
    btn: "bg-emerald-600 hover:bg-emerald-700",
  },
};

/** Compact, responsive multi-column accordion grid for issue/fault lists. */
export function IssueGrid({ items, tone, cta, testIdPrefix }: { items: IssueItem[]; tone: Tone; cta: string; testIdPrefix: string }) {
  const t = tones[tone];
  return (
    <Accordion type="single" collapsible className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-start">
      {items.map((item) => (
        <AccordionItem
          key={item.id}
          value={item.id}
          data-testid={`${testIdPrefix}-${item.id}`}
          className={cn("group bg-card border border-border rounded-xl shadow-sm ring-0 data-[state=open]:ring-2 transition-colors", t.open)}
        >
          <AccordionTrigger className="hover:no-underline min-h-[56px] px-3.5 py-2.5 gap-2" data-testid={`button-${testIdPrefix}-${item.id}`}>
            <span className="flex items-center gap-3 text-left">
              <span className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors [&>svg]:w-5 [&>svg]:h-5", t.icon)}>
                {item.icon}
              </span>
              <span className="text-[15px] font-semibold font-display leading-tight">{item.title}</span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3.5 pb-3.5 text-sm text-muted-foreground leading-relaxed">
            <p className="mb-3">{item.desc}</p>
            <Button
              size="sm"
              onClick={() => document.getElementById("get-in-touch")?.scrollIntoView({ behavior: "smooth" })}
              className={cn("text-white rounded-full min-h-[44px] w-full px-2 whitespace-normal text-xs leading-snug", t.btn)}
            >
              {cta}
            </Button>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
